import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  resetForgotPassword,
  sendForgotPasswordOtp,
  verifyForgotPasswordOtp
} from "./api/authApi";
import { sanitizeApiMessage } from "./auth/clientAuth";
import WillPlayMotionLogo from "./components/WillPlayMotionLogo";
import PasswordInput from "./PasswordInput";

function validateStrongPassword(value) {
  const password = String(value || "");
  if (password.length < 8) return "Password must be at least 8 characters.";
  if (/\s/.test(password)) return "Password cannot contain spaces.";
  if (!/[A-Z]/.test(password)) return "Password must include at least one uppercase letter.";
  if (!/[a-z]/.test(password)) return "Password must include at least one lowercase letter.";
  if (!/[0-9]/.test(password)) return "Password must include at least one number.";
  if (!/[^A-Za-z0-9]/.test(password)) return "Password must include at least one special character.";
  return "";
}

function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("identify");
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  const passwordMessage = useMemo(() => validateStrongPassword(password), [password]);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const t = window.setInterval(() => {
      setResendSeconds((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [resendSeconds]);

  const doSendOtp = async () => {
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const { response, data } = await sendForgotPasswordOtp(usernameOrEmail.trim());
      if (!response.ok) {
        const raw =
          (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
          "Could not send code.";
        setError(sanitizeApiMessage(raw));
        return;
      }
      const msg =
        (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
        "If an account exists for that email or username, we sent a 6-digit code.";
      setInfo(sanitizeApiMessage(msg));
      setStep("otp");
      setResendSeconds(60);
    } catch (apiError) {
      console.error(apiError);
      setError("Cannot reach the server. Confirm the API is running.");
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = (e) => {
    e.preventDefault();
    void doSendOtp();
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const { response, data } = await verifyForgotPasswordOtp({
        usernameOrEmail: usernameOrEmail.trim(),
        code: otpCode.trim()
      });
      if (!response.ok) {
        const raw =
          (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
          "Invalid or expired code.";
        setError(sanitizeApiMessage(raw));
        return;
      }
      const token =
        (data && typeof data === "object" && typeof data.resetToken === "string" && data.resetToken) ||
        (data && typeof data === "object" && typeof data.ResetToken === "string" && data.ResetToken) ||
        "";
      if (!token) {
        setError("Server did not return a reset token.");
        return;
      }
      setResetToken(token);
      setStep("password");
    } catch (apiError) {
      console.error(apiError);
      setError("Cannot reach the server. Confirm the API is running.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    const passwordError = validateStrongPassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { response, data } = await resetForgotPassword({
        resetToken,
        password,
        confirmPassword
      });
      if (!response.ok) {
        const raw =
          (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
          "Could not update password.";
        setError(sanitizeApiMessage(raw));
        return;
      }
      const note =
        (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
        "Password updated. Sign in with your email or username and new password.";
      navigate("/login", { state: { registrationNote: sanitizeApiMessage(note) } });
    } catch (apiError) {
      console.error(apiError);
      setError("Cannot reach the server. Confirm the API is running.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-page">
      {step === "identify" ? (
        <form className="auth-card" onSubmit={sendOtp}>
          <div className="auth-brand">
            <WillPlayMotionLogo size={30} className="auth-brand-logo" />
            <span>WillPlay</span>
          </div>
          <h1>Forgot password</h1>
          <p className="auth-step-hint">
            Enter the email or username for your account. We will email you a 6-digit code if we find
            it.
          </p>
          <input
            type="text"
            placeholder="Email or username"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />
          {error ? <p className="error-text">{error}</p> : null}
          {info ? <p className="success-text">{info}</p> : null}
          <button className="primary-btn" type="submit" disabled={loading}>
            {loading ? "Sending…" : "Send code"}
          </button>
          <p className="auth-back-link">
            <Link to="/login">Back to login</Link>
          </p>
        </form>
      ) : null}

      {step === "otp" ? (
        <form className="auth-card" onSubmit={verifyOtp}>
          <div className="auth-brand">
            <WillPlayMotionLogo size={30} className="auth-brand-logo" />
            <span>WillPlay</span>
          </div>
          <h1>Enter code</h1>
          <p className="auth-step-hint">
            We sent a code to the email on file for{" "}
            <strong className="auth-email-highlight">{usernameOrEmail.trim()}</strong>.
          </p>
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="6-digit code"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            required
          />
          {info ? <p className="success-text">{info}</p> : null}
          {error ? <p className="error-text">{error}</p> : null}
          <button className="primary-btn" type="submit" disabled={loading || otpCode.length !== 6}>
            {loading ? "Checking…" : "Verify code"}
          </button>
          <div className="auth-resend-row">
            <button
              type="button"
              className="ghost-btn small"
              disabled={loading || resendSeconds > 0}
              onClick={() => {
                setOtpCode("");
                void doSendOtp();
              }}
            >
              {resendSeconds > 0 ? `Resend in ${resendSeconds}s` : "Resend code"}
            </button>
          </div>
          <p className="auth-back-link">
            <button type="button" className="ghost-btn small" onClick={() => setStep("identify")}>
              Change email / username
            </button>
          </p>
        </form>
      ) : null}

      {step === "password" ? (
        <form className="auth-card" onSubmit={resetPassword}>
          <div className="auth-brand">
            <WillPlayMotionLogo size={30} className="auth-brand-logo" />
            <span>WillPlay</span>
          </div>
          <h1>New password</h1>
          <p className="auth-step-hint">Choose a new password for your account.</p>
          <PasswordInput
            placeholder="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            name="new-password"
          />
          {password.length > 0 && passwordMessage ? (
            <p className="error-text">{passwordMessage}</p>
          ) : null}
          <PasswordInput
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
            name="confirm-new-password"
          />
          {confirmPassword.length > 0 && password !== confirmPassword ? (
            <p className="error-text">Passwords do not match.</p>
          ) : null}
          {error ? <p className="error-text">{error}</p> : null}
          <button
            className="primary-btn"
            type="submit"
            disabled={loading || Boolean(passwordMessage) || password !== confirmPassword}
          >
            {loading ? "Saving…" : "Update password"}
          </button>
          <p className="auth-back-link">
            <Link to="/login">Back to login</Link>
          </p>
        </form>
      ) : null}
    </main>
  );
}

export default ForgotPassword;
